import { Button, Card, Center, Loader } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconArrowBackUp, IconEdit, IconTrash } from '@tabler/icons-react';
import PageContent from '../../../../components/core/PageContent/PageContent';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import { Permission } from '../../../../models/core/departments.type';
import { PackagingType } from '../../../../models/core/packaging.type';
import packagingsPackaging from '../../../../services/core/packaging.service';
import { Feature } from '../../../../utils/constants.utils';
import DataView from './components/DataView';

type Result = {
  loading: boolean;
  data: PackagingType | null;
};

function PackagingView() {
  const navigate = useNavigate();
  const location: any = useLocation();
  const modals = useModals();
  const { idResiduoAcondicionamento } = useParams();

  const [result, setResult] = useState<Result>({
    loading: location?.state?.packaging === undefined,
    data: location?.state?.packaging || null,
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      let data: PackagingType | null = null;
      try {
        data = await packagingsPackaging.select({
          idResiduoAcondicionamento: Number(idResiduoAcondicionamento),
        });
      } catch (error: any) {
        showNotification({
          title: 'Acondicionamento - Visualizar',
          message: error?.isBusinessException
            ? error.description
            : `Não foi possível carregar o acondicionamento.`,
          color: 'red',
        });
        navigate(`/${error?.statusCode || '500'}`, {
          replace: true,
        });
      } finally {
        setResult({ loading: false, data });
      }
    };
    if (result.data === null) {
      fetchData();
    }
  }, [idResiduoAcondicionamento, navigate, result]);

  const handleAction = async () => {
    try {
      setResult({ loading: false, data: result.data });

      setDeleting(true);
      await packagingsPackaging.delete({ idResiduoAcondicionamento: Number(idResiduoAcondicionamento) });
      navigate('/packaging');

      showNotification({
        title: 'Acondicionamento - Visualizar',
        message: `Acondicionamento excluído com sucesso.`,
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Acondicionamento - Visualizar',
        message: error?.isBusinessException
          ? error.description
          : `Não foi possível excluir o acondicionamento.`,
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  };

  const showModal = () =>
    modals.openConfirmModal({
      title: `Gostaria de excluir esse acondicionamento?`,
      size: 'sm',
      closeOnClickOutside: false,
      closeOnEscape: false,
      onConfirm: () => {
        handleAction();
      },
    });

  return (
    <Card>
      <PageHeader
        feature={Feature.Reference.Packaging}
        title="Acondicionamento - Visualizar"
        description="Visualize os detalhes de um acondicionamento."
        buttons={[
          <Button
            key="Voltar"
            color="secondary"
            leftSection={<IconArrowBackUp size={18} />}
            onClick={() => {
              navigate(-1);
            }}
            disabled={deleting || result.loading}
          >
            Voltar
          </Button>,
          <Button
            key="Excluir"
            color="red"
            leftSection={<IconTrash size={18} />}
            onClick={() => {
              showModal();
            }}
            disabled={deleting || result.loading}
            loading={deleting}
            data-permission={Permission.PackagingDelete}
          >
            Excluir
          </Button>,
          <Button
            key="Editar"
            color="accent"
            leftSection={<IconEdit size={18} />}
            onClick={() => {
              navigate(`/packaging/${result?.data?.idResiduoAcondicionamento}/edit`, {
                state: {
                  packaging: result.data,
                },
              });
            }}
            disabled={deleting || result.loading}
            data-permission={Permission.PackagingEdit}
          >
            Editar
          </Button>,
        ]}
      />
      {result.data === null ? (
        <Center>
          <Loader size="xl" />
        </Center>
      ) : (
        <PageContent>
          <DataView data={result.data} />
        </PageContent>
      )}
    </Card>
  );
}

export default PackagingView;
